import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  FaMobileAlt,
  FaSpinner,
  FaSync,
  FaArrowLeft,
  FaLock,
  FaInfoCircle,
  FaExclamationTriangle
} from 'react-icons/fa';
import { useAuthContext } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import paymentApi from '../../../api/payments/paymentApi';
import './PaymentModal.css';

const PaymentModal = ({
  show,
  handleClose,
  amount = 0,
  title = "Complete Payment",
  summaryChildren,
  paymentId,
  initialPhoneNumber = '',
  initialPaymentMethod = '',
  onPaymentSuccess,
  onPaymentError,
  mnoOptions = []
}) => {
  const { isAuthenticated } = useAuthContext();
  const [selectedPayment, setSelectedPayment] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || '');
  const [localLoading, setLocalLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [fetchedMnos, setFetchedMnos] = useState([]);
  const [fetchingMnos, setFetchingMnos] = useState(false);
  const [promptSent, setPromptSent] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const pollIntervalRef = useRef(null);
  const hasFetchedMnosRef = useRef(false);

  const clearPoll = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const resetState = () => {
    clearPoll();
    hasFetchedMnosRef.current = false;
    setSelectedPayment('');
    setPhoneNumber(initialPhoneNumber || '');
    setLocalLoading(false);
    setStatusMessage('');
    setPromptSent(false);
    setCheckingStatus(false);
  };

  useEffect(() => {
    const loadMnos = async () => {
      if (hasFetchedMnosRef.current) return;
      hasFetchedMnosRef.current = true;

      setFetchingMnos(true);
      try {
        const res = await paymentApi.getAvailableMnos();
        const list =
          res?.data?.data?.mno_options ||
          res?.data?.mno_options ||
          res?.mno_options ||
          res?.data?.data ||
          res?.data ||
          [];
        if (Array.isArray(list) && list.length > 0) {
          setFetchedMnos(list);
        }
      } catch (err) {
        console.error('Error loading available MNOs:', err);
      } finally {
        setFetchingMnos(false);
      }
    };

    if (show) {
      if (initialPhoneNumber) setPhoneNumber(initialPhoneNumber);
      if (!mnoOptions || mnoOptions.length === 0) {
        loadMnos();
      }
    } else {
      resetState();
    }
  }, [show, mnoOptions?.length, initialPhoneNumber]);

  const optionsToUse = (mnoOptions && mnoOptions.length > 0) ? mnoOptions : fetchedMnos;
  const paymentMethods = optionsToUse.map(opt => ({
    id: opt.paymentname || opt.payment_name || opt.id || opt.name,
    name: opt.displayname || opt.display_name || opt.name || opt.paymentname,
    logo: opt.logo || opt.image || null
  }));

  useEffect(() => {
    if (paymentMethods.length > 0) {
      const exists = paymentMethods.some(m => m.id === selectedPayment);
      if (!exists) {
        const match = initialPaymentMethod
          ? paymentMethods.find(m => String(m.id).toLowerCase() === String(initialPaymentMethod).toLowerCase())
          : null;
        setSelectedPayment(match ? match.id : paymentMethods[0].id);
      }
    }
  }, [paymentMethods, selectedPayment, initialPaymentMethod]);

  const selectedMethodObj = paymentMethods.find(m => m.id === selectedPayment);

  const handleManualCheckStatus = async () => {
    if (!paymentId) return;
    setCheckingStatus(true);
    try {
      const statusRes = await paymentApi.checkPaymentStatus(paymentId);
      const data = statusRes?.data?.data || statusRes?.data || statusRes || {};

      if (data.status === 1 || data.payment?.status === 1) {
        clearPoll();
        toast.success('Malipo yamekamilika kikamilifu!');
        if (onPaymentSuccess) onPaymentSuccess(data);
        handleModalClose();
      } else if (data.status === 2 || data.payment?.status === 2) {
        clearPoll();
        toast.error('Malipo yameshindwa kwenye mtandao.');
        if (onPaymentError) onPaymentError(new Error('Payment failed'));
      } else {
        toast.info('Malipo bado yanashughulikiwa. Tafadhali hakikisha umeingiza PIN kwenye simu yako.');
      }
    } catch (err) {
      console.error('Status check error:', err);
      toast.error('Imeshindwa kukagua hali ya malipo kwa sasa.');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!selectedPayment) {
      toast.warning('Tafadhali chagua njia ya malipo');
      return;
    }
    if (!phoneNumber) {
      toast.warning('Tafadhali ingiza namba ya simu');
      return;
    }

    setLocalLoading(true);
    setStatusMessage('Inatuma ombi la malipo kwenda kwenye simu yako...');

    try {
      const chargeData = {
        payment_id: paymentId,
        mno: selectedPayment,
        phone_number: phoneNumber
      };

      await paymentApi.chargePayment(chargeData);

      setPromptSent(true);
      setLocalLoading(false);
      setStatusMessage('Ingiza PIN kwenye simu yako kukamilisha malipo...');
      toast.info('Ombi la malipo limetumwa! Tafadhali ingiza PIN kwenye simu yako.');

      let attempts = 0;
      const maxAttempts = 8;

      clearPoll();
      pollIntervalRef.current = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await paymentApi.checkPaymentStatus(paymentId);
          const data = statusRes?.data?.data || statusRes?.data || statusRes || {};

          if (data.status === 1 || data.payment?.status === 1) {
            clearPoll();
            toast.success('Malipo yamekamilika kikamilifu!');
            if (onPaymentSuccess) onPaymentSuccess(data);
            handleModalClose();
          } else if (data.status === 2 || data.payment?.status === 2) {
            clearPoll();
            toast.error('Malipo yameshindwa kwenye mtandao.');
            if (onPaymentError) onPaymentError(new Error('Payment failed'));
          }
        } catch (pollErr) {
          console.error('Status polling error:', pollErr);
        }

        if (attempts >= maxAttempts) {
          clearPoll();
          setStatusMessage('Ingiza PIN ukimaliza, unaweza kukagua kwenye Payment History.');
        }
      }, 15000);

    } catch (err) {
      console.error('Error charging payment:', err);
      setLocalLoading(false);
      setStatusMessage('');
      if (onPaymentError) onPaymentError(err);
    }
  };

  const handleModalClose = () => {
    resetState();
    handleClose();
  };

  if (!isAuthenticated) return null;

  return (
    <Modal show={show} onHide={handleModalClose} centered size="md" backdrop="static">
      <Modal.Body className="p-0">
        <div className="payment-container">
          <h2 className="stylish-heading-payment text-center mt-3">{title}</h2>

          <div className="payment-summary shadow-sm">
            <h5 className="fw-bold mb-3 text-secondary">Order Summary</h5>

            <div className="payment-summary-details">
              {summaryChildren}
            </div>

            <div className="payment-summary-total">
              <span>Total Amount Due</span>
              <strong className="text-primary">TZS {Number(amount).toLocaleString()}</strong>
            </div>
          </div>

          {promptSent ? (
            <div className="p-3 text-center my-3 bg-light rounded-3 border">
              <div className="badge bg-success fs-6 mb-2 p-2 rounded-pill d-inline-flex align-items-center gap-2">
                <FaMobileAlt /> Ombi la Malipo Limetumwa (Push Sent)
              </div>
              <p className="fw-semibold text-dark mb-3" style={{ fontSize: '0.95rem' }}>
                Tafadhali ingiza Mobile Money PIN kwenye simu yako <strong>{phoneNumber}</strong> ili kuthibitisha malipo ya <strong>TZS {Number(amount).toLocaleString()}</strong>.
              </p>

              <div className="d-flex flex-column gap-2 justify-content-center">
                <Button
                  variant="primary"
                  className="w-100 fw-bold py-2 d-flex align-items-center justify-content-center gap-2"
                  onClick={handleManualCheckStatus}
                  disabled={checkingStatus}
                >
                  {checkingStatus ? (
                    <>
                      <FaSpinner className="fa-spin" /> Inakagua Hali ya Malipo...
                    </>
                  ) : (
                    <>
                      <FaSync /> Thibitisha Hali ya Malipo (Check Status)
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  className="w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                  onClick={handleModalClose}
                >
                  <FaArrowLeft /> Funga Dirisha (Close)
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h5 className="fw-bold mb-3 mt-4 text-center text-secondary">Select Payment Method</h5>

              {fetchingMnos ? (
                <div className="text-center py-4 text-muted">
                  <FaSpinner className="fa-spin me-2 text-primary" />
                  Inapakia mitandao ya malipo...
                </div>
              ) : paymentMethods.length > 0 ? (
                <div className="payment-methods">
                  {paymentMethods.map((method) => (
                    <div
                      key={method.id}
                      className={`payment-method ${selectedPayment === method.id ? 'selected' : ''} ${localLoading ? 'disabled' : ''}`}
                      onClick={() => !localLoading && setSelectedPayment(method.id)}
                    >
                      {method.logo ? (
                        <img src={method.logo} alt={method.name} />
                      ) : (
                        <div className="fw-bold text-primary p-2">{method.name}</div>
                      )}
                      <div className="payment-method-name">{method.name}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted my-3 d-flex align-items-center justify-content-center gap-2">
                  <FaExclamationTriangle className="text-warning" /> Hakuna mitandao ya malipo iliyopatikana.
                </div>
              )}

              {selectedPayment && (
                <div className="phone-input-wrapper animate-fade-in">
                  <label htmlFor="paymentPhoneNumber">Mobile Money Number</label>
                  <input
                    id="paymentPhoneNumber"
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 0712345678"
                    value={phoneNumber}
                    disabled={localLoading}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                      setPhoneNumber(numericValue);
                    }}
                    pattern="[0-9]*"
                  />
                  <small className="text-muted mt-1 d-block">Enter the number that will receive the payment prompt.</small>
                </div>
              )}

              {statusMessage && (
                <div className="text-center mt-3 mb-2 text-primary fw-semibold d-flex align-items-center justify-content-center gap-2" style={{ fontSize: '0.9rem' }}>
                  <FaSpinner className="fa-spin" />
                  {statusMessage}
                </div>
              )}

              <div className="payment-actions">
                <button className="btn-back" onClick={handleModalClose} disabled={localLoading}>
                  <FaArrowLeft className="me-2" /> Close
                </button>
                <button
                  className="btn-pay d-flex align-items-center justify-content-center gap-2"
                  onClick={handleSubmit}
                  disabled={localLoading || !selectedPayment || !phoneNumber}
                >
                  {localLoading ? (
                    <>
                      Processing <FaSpinner className="fa-spin" />
                    </>
                  ) : (
                    <>
                      Pay TZS {Number(amount).toLocaleString()} <FaLock />
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PaymentModal;
